// src/components/sat/HourDonut.js
//
// Small, dependency-free SVG donut chart for planned-hour allocation between
// subjects (e.g. Reading & Writing vs Math). Shared by the plan workspace and
// the student Plan detail page so they speak the same visual language. Kept
// intentionally simple — parent-friendly and easy to explain.

import React from "react";

export const SUBJECT_COLORS = {
  // SAT
  "Reading & Writing": "#6366f1",
  Math: "#0ea5e9",
  // ACT
  English: "#6366f1",
  Reading: "#10b981",
  Science: "#f59e0b",
};

// Fallback palette so custom plans (arbitrary subject names) always get a colour.
export const EXTRA_SUBJECT_COLORS = [
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#ef4444",
  "#84cc16",
  "#06b6d4",
  "#a855f7",
];

/** Colour for a subject: known SAT/ACT colours first, then the fallback palette. */
export function colorForSubject(subject, index = 0) {
  return SUBJECT_COLORS[subject] || EXTRA_SUBJECT_COLORS[index % EXTRA_SUBJECT_COLORS.length];
}

export default function HourDonut({ segments = [], size = 132, thickness = 18, centerTop, centerBottom }) {
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  let offset = 0;

  return (
    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${cx} ${cy})`}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eef2f7" strokeWidth={thickness} />
          {total > 0 &&
            segments
              .filter((s) => (s.value || 0) > 0)
              .map((s, i) => {
                const len = (s.value / total) * circ;
                const el = (
                  <circle
                    key={i}
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={thickness}
                    strokeDasharray={`${len} ${circ - len}`}
                    strokeDashoffset={-offset}
                    strokeLinecap="butt"
                  >
                    <title>{`${s.label}: ${s.value}h`}</title>
                  </circle>
                );
                offset += len;
                return el;
              })}
        </g>
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
          {centerTop != null ? centerTop : `${total}h`}
        </div>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#94a3b8", marginTop: 3 }}>
          {centerBottom != null ? centerBottom : "Planned"}
        </div>
      </div>
    </div>
  );
}

/** Legend row for the donut (label + colored dot + hours). */
export function DonutLegend({ segments = [] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {segments.map((s) => (
        <span key={s.label} style={{ display: "inline-flex", alignItems: "center", fontSize: 13, color: "#334155" }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: s.color, marginRight: 8, display: "inline-block" }} />
          {s.label}
          <strong style={{ marginLeft: 6 }}>{s.value}h</strong>
        </span>
      ))}
    </div>
  );
}
